import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';

export default function AboutPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于不良研究所',
    description: '不良研究所是一个专注深度内容研究与创意探索的平台，汇聚多元视角与独立思考。',
    url: 'https://buliangyanjiusuo.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '不良研究所',
      url: 'https://buliangyanjiusuo.com',
      description: '专注深度内容研究与创意探索的平台',
      foundingDate: '2024',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@buliangyanjiusuo.com',
        availableLanguage: ['Chinese'],
      },
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '不良研究所的内容有什么特点？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不良研究所的内容以深度、独立、多元为核心特点。每篇文章都经过严谨的研究和编辑过程，力求在信息洪流中为读者提供真正有价值的深度阅读体验。我们关注那些被主流叙事忽略的重要议题，从社会观察到文化研究，从科技前沿到历史探索，覆盖多个领域。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在不良研究所投稿？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们欢迎独立作者和研究者投稿。请将您的稿件大纲或完整文章发送至 contact@buliangyanjiusuo.com，我们的编辑团队会在7个工作日内回复。我们尤其欢迎具有独立思考、深度研究和独特视角的内容。',
        },
      },
      {
        '@type': 'Question',
        name: '不良研究所的内容是否收费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不良研究所目前所有内容均为免费阅读。我们相信优质内容应该被更多人看到。未来我们可能会推出付费会员服务，提供更深度的研究内容和独家分析，但基础内容将始终保持免费。',
        },
      },
      {
        '@type': 'Question',
        name: '如何订阅不良研究所的更新？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页底部的订阅表单输入邮箱地址，订阅我们的研究简报。每周我们会将精选内容发送到您的邮箱。您也可以关注我们的微信公众号、微博和B站账号获取最新动态。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 不良研究所"
        description="不良研究所是一个专注深度内容研究与创意探索的平台，汇聚多元视角与独立思考。了解我们的品牌故事、团队、行业声誉与联系方式。"
        keywords="不良研究所,关于不良研究所,深度研究内容平台,独立创意研究,不良研究所团队"
        canonicalUrl="https://buliangyanjiusuo.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([jsonLd, faqJsonLd]) }}
      />
      <div className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <BrandIntro />
        <MissionVision />
        <Reputation />
        <TeamSection />
        <FAQSection />
        <ContactSection />
      </div>
    </>
  );
}

function BrandIntro() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
      <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
        <div className="flex-1">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 leading-tight"
            style={{ fontFamily: 'var(--font-heading)' }}>
            关于 <span className="text-primary-600">不良研究所</span>
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
            不良研究所成立于2024年，是一个专注深度内容研究与创意探索的平台。
            我们的名字"不良"并非否定，而是一种态度——不满足于表面的"好"，
            不盲从主流的"对"，始终保持对世界的独立思考和批判性审视。
          </p>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
            在信息碎片化和算法投喂的时代，我们致力于做一件"逆潮流"的事：提供需要时间阅读、
            需要思考消化的深度内容。每一篇文章都是一次严肃而有趣的研究旅程——我们相信，
            真正有价值的知识不会以短平快的方式呈现，深度的理解需要耐心和专注。
          </p>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
            我们汇聚了来自社会学、文化研究、科技评论、历史学、艺术批评等领域的独立作者和研究者。
            他们不追逐热点，不迎合流量，而是以严谨的研究方法和独特的个人视角，
            为读者呈现那些被主流叙事忽略的重要议题和精彩故事。
          </p>
        </div>
        <div className="flex-1">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20library%20or%20study%20room%20with%20bookshelves%20and%20soft%20natural%20light%2C%20cream%20and%20terracotta%20tones%2C%20intellectual%20atmosphere%2C%20cozy%20reading%20corner%2C%20editorial%20interior%20photography&width=800&height=600&seq=about-brand-intro&orientation=landscape"
            alt="不良研究所品牌形象 - 充满书香的研究空间"
            className="w-full rounded-lg object-cover"
          />
        </div>
      </div>
    </section>
  );
}

function MissionVision() {
  return (
    <section className="bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-6">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600">
              <i className="ri-focus-3-line text-lg"></i>
            </div>
            <h3 className="mt-4 text-lg font-bold text-foreground-900">我们的使命</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              在信息泛滥的时代，为读者筛选和创作真正有深度、有价值的原创内容，
              培养独立思考的能力和习惯，推动理性公共讨论的文化。
            </p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100/80 text-accent-600">
              <i className="ri-eye-line text-lg"></i>
            </div>
            <h3 className="mt-4 text-lg font-bold text-foreground-900">我们的愿景</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              成为中国最具影响力的独立深度内容平台，建立一个汇聚多元视角、
              鼓励独立思考的知识社区，让深度阅读重新成为人们的生活方式。
            </p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-heart-line text-lg"></i>
            </div>
            <h3 className="mt-4 text-lg font-bold text-foreground-900">我们的价值观</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              独立、严谨、多元、开放。不为流量妥协内容质量，不为立场放弃事实核查，
              不为效率牺牲阅读体验，不为多数压制少数声音。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Reputation() {
  const recognitions = [
    {
      year: '2026',
      title: '年度最佳独立内容平台',
      source: '中国数字媒体协会',
      description: '表彰不良研究所在深度内容创作和独立媒体实践方面的杰出贡献',
    },
    {
      year: '2025',
      title: '最具影响力文化媒体',
      source: '新媒体排行榜',
      description: '在文化类自媒体中综合影响力排名前十，原创内容质量评分领先',
    },
    {
      year: '2025',
      title: '优质内容创作者',
      source: '腾讯内容开放平台',
      description: '连续六个月获得"优质原创内容"认证，文章平均阅读完成率超过85%',
    },
    {
      year: '2024',
      title: '年度新锐媒体',
      source: '搜狐传媒',
      description: '创刊第一年即获得广泛关注，多篇深度报道被主流媒体转发引用',
    },
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          行业认可与声誉
        </h2>
        <p className="mt-2 text-sm text-foreground-600">成立以来获得的专业认可与荣誉</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {recognitions.map((item, index) => (
          <div key={index} className="bg-background-50 rounded-lg p-6 flex gap-4">
            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-accent-100/80 text-accent-600 font-bold text-sm">
              {item.year}
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground-900">{item.title}</h3>
              <p className="text-xs text-foreground-500 mt-1">{item.source}</p>
              <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TeamSection() {
  const team = [
    {
      name: '林思远',
      role: '创始人 & 主编',
      bio: '社会学者，前媒体人。专注数字社会与身份政治研究，相信深度阅读是抵抗碎片化时代的最好方式。',
    },
    {
      name: '陈雨桐',
      role: '联合创始人 & 运营总监',
      bio: '可持续消费研究者，关注消费文化与生态伦理。负责平台内容策略与社区运营。',
    },
    {
      name: '张启明',
      role: '科技板块主编',
      bio: '科技伦理研究者，人工智能治理方向博士。致力于用通俗语言讲述复杂的科技议题。',
    },
    {
      name: '周明哲',
      role: '文化板块主编',
      bio: '文化研究学者，专注当代中国文化变迁。相信每一个文化现象背后都有一个值得被讲述的故事。',
    },
    {
      name: '苏曼青',
      role: '艺术板块主编',
      bio: '艺术评论人，当代艺术理论与策展方向。以批判性视角审视当代艺术实践，连接艺术与公众。',
    },
    {
      name: '王涵之',
      role: '历史板块主编',
      bio: '历史学者，专攻丝绸之路与跨文化交流史。致力于将学术研究的成果转化为公众可理解的知识。',
    },
  ];

  return (
    <section className="bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            核心团队
          </h2>
          <p className="mt-2 text-sm text-foreground-600">一群热爱深度研究和内容创作的人</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {team.map((member) => (
            <div key={member.name} className="bg-background-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary-100/70 flex items-center justify-center text-primary-600 font-bold text-lg">
                {member.name[0]}
              </div>
              <h3 className="mt-3 text-sm font-bold text-foreground-900">{member.name}</h3>
              <p className="text-xs text-primary-600 font-medium mt-0.5">{member.role}</p>
              <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{member.bio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    {
      q: '不良研究所的内容有什么特点？',
      a: '不良研究所的内容以深度、独立、多元为核心特点。每篇文章都经过严谨的研究和编辑过程，力求在信息洪流中为读者提供真正有价值的深度阅读体验。我们关注那些被主流叙事忽略的重要议题，从社会观察到文化研究，从科技前沿到历史探索，覆盖多个领域。',
    },
    {
      q: '如何在不良研究所投稿？',
      a: '我们欢迎独立作者和研究者投稿。请将您的稿件大纲或完整文章发送至 contact@buliangyanjiusuo.com，我们的编辑团队会在7个工作日内回复。我们尤其欢迎具有独立思考、深度研究和独特视角的内容。',
    },
    {
      q: '不良研究所的内容是否收费？',
      a: '不良研究所目前所有内容均为免费阅读。我们相信优质内容应该被更多人看到。未来我们可能会推出付费会员服务，提供更深度的研究内容和独家分析，但基础内容将始终保持免费。',
    },
    {
      q: '如何订阅不良研究所的更新？',
      a: '您可以通过首页底部的订阅表单输入邮箱地址，订阅我们的研究简报。每周我们会将精选内容发送到您的邮箱。您也可以关注我们的微信公众号、微博和B站账号获取最新动态。',
    },
    {
      q: '不良研究所的文章是否可以被转载？',
      a: '我们鼓励优质内容的传播。如需转载，请通过邮箱 contact@buliangyanjiusuo.com 联系我们的编辑团队获取授权。转载时需注明出处和原作者信息，并保留原文链接。未经授权的商业转载将追究法律责任。',
    },
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          常见问题
        </h2>
        <p className="mt-2 text-sm text-foreground-600">关于不良研究所，你可能想了解的信息</p>
      </div>

      <div className="max-w-3xl mx-auto divide-y divide-background-200/70">
        {faqs.map((faq, index) => (
          <details key={index} className="group py-5 cursor-pointer">
            <summary className="flex items-center justify-between gap-4 text-sm font-semibold text-foreground-900 list-none">
              {faq.q}
              <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
            </summary>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              {faq.a}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section className="bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            联系我们
          </h2>
          <p className="mt-2 text-sm text-foreground-600">期待与你的交流与合作</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-background-50 rounded-lg p-6 text-center">
            <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600">
              <i className="ri-mail-line"></i>
            </div>
            <h3 className="mt-3 text-sm font-semibold text-foreground-900">电子邮箱</h3>
            <p className="mt-1 text-xs text-foreground-600">contact@buliangyanjiusuo.com</p>
            <p className="text-xs text-foreground-500 mt-0.5">投稿与合作咨询</p>
          </div>
          <div className="bg-background-50 rounded-lg p-6 text-center">
            <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100/80 text-accent-600">
              <i className="ri-wechat-line"></i>
            </div>
            <h3 className="mt-3 text-sm font-semibold text-foreground-900">微信公众号</h3>
            <p className="mt-1 text-xs text-foreground-600">不良研究所</p>
            <p className="text-xs text-foreground-500 mt-0.5">搜索关注获取每日精选</p>
          </div>
          <div className="bg-background-50 rounded-lg p-6 text-center">
            <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-bilibili-line"></i>
            </div>
            <h3 className="mt-3 text-sm font-semibold text-foreground-900">B站账号</h3>
            <p className="mt-1 text-xs text-foreground-600">不良研究所Official</p>
            <p className="text-xs text-foreground-500 mt-0.5">视频内容与直播</p>
          </div>
        </div>
      </div>
    </section>
  );
}